import {
  takeLatest,
  select,
  put,
  call,
  take,
  delay,
  cancel,
  fork,
  race,
} from 'redux-saga/effects';
import get from 'lodash/get';

import { handlePriceCalculations } from '../../appointmentForm/sagas/paymentUrgentVisit';
import * as couponCalculation from '../requests/coupon-price-calculation-req';
import * as paymentOrder from '../requests/payment-order-req';
import * as payaRedirection from '../requests/paya-redirection-req';
import * as paymentVerification from '../requests/payment-verifications-req';
import * as couponVerification from '../requests/coupon-verification-req';
import * as popup from '../../ui/popup';
import * as error from '../../ui/error';
import * as success from '../../ui/success';
import { selectCurrentAppointment } from '../../appointmnents/selectors';
import {
  selectProgressIsUrgentVisitFlow,
  selectAppointmentPaymentInfoModalOpened,
} from '../../appointmentForm/selectors';
import {
  selectActiveAppointment,
  selectActiveAppointmentCoupon,
  selectActiveAppointmentId,
  selectRoomInvitationInfoUserId,
  selectRoomInvitationInfoAppointmentId,
} from '../selectors';

import { UI_POPUP_TYPES } from '../../../constants/props';
import { PAYMENT_METHODS } from '../../../constants/payment-methods';
import {
  SUBSCRIBABLE_TYPE,
  PAYMENT_STATUSES,
  URGENT_VISIT_TYPE_CODE,
  FAILED_PAYMENT_STATUSES,
} from '../../../constants';

import { actions as accountVaultsFormActions } from '../../programs/accountVaultsForm';

import { loggedUserIdSelector } from '../../context/selectors';

import {
  actions as accountVaultsActions,
  consts as accountVaultsConsts,
} from '../../programs/accountVaults';

import {
  appointmentDetailsActions,
  APPOINTMENT_DETAILS_TYPES,
} from '../appointment-details';

import { appointmentPaymentInfoModalActions } from '../../appointmentForm/appointment-payment-info-modal';
import { consts as appointmentAccountFormPaymentConst } from '../../appointmnents/requests/appointment-account-form-payment-req';

import { selectPaperworkAppointmentId } from '../../paperwork/selectors/paperwork-get-appointment-details';

const {
  START_PAYMENT_PROCESS,
  PAYMENT_FAIL,
  PAYMENT_SUCCESS,
  PAYMENT_TIMEOUT,
} = APPOINTMENT_DETAILS_TYPES;

function* handleAccountFormPaymentSaga(payload) {
  const { location, paymentOrderId } = payload || {};

  const loggedInUserId = yield select(loggedUserIdSelector);
  const roomInvitationUserId = yield select(selectRoomInvitationInfoUserId);
  const userId = loggedInUserId || roomInvitationUserId;

  const { id: locationId } = location || {};

  yield put(
    accountVaultsActions.send({
      userId,
      subjectId: locationId,
      subjectType: SUBSCRIBABLE_TYPE.LOCATION,
    }),
  );

  const { successAccountVaults, errorAccountVaults } = yield race({
    successAccountVaults: take(accountVaultsConsts.SUCCEED),
    errorAccountVaults: take(accountVaultsConsts.REJECTED),
  });

  if (errorAccountVaults) {
    yield put(
      error.actions.update({
        id: 'errors.common',
      }),
    );
  } else {
    const isAtLeastOneVaultExists = Boolean(
      get(successAccountVaults, 'payload.length', 0),
    );

    const props = {
      locationId,
      userId,
    };

    yield put(
      appointmentPaymentInfoModalActions.open({
        ...props,
        paymentOrderId,
      }),
    );

    if (!isAtLeastOneVaultExists) {
      yield put(accountVaultsFormActions.send(props));

      yield put({
        type: popup.consts.SHOW,
        payload: {
          contentType: UI_POPUP_TYPES.PAYA_ACCOUNT_FORM,
          contentOptions: {
            noCards: true,
          },
        },
      });
    }
  }
}

function* handleCouponCalculationSaga({ payload }) {
  const { coupon } = payload;
  yield take(couponCalculation.consts.SUCCEED);
  yield put(appointmentDetailsActions.setCoupon(coupon));
}

function* runPayaPaymentFlow(paymentOrderId) {
  yield put(payaRedirection.actions.send({ paymentOrderId }));

  const { payload } = yield take(payaRedirection.consts.SUCCEED);

  const { url } = payload;

  yield put(
    popup.actions.show({
      contentType: UI_POPUP_TYPES.PAYA_PAYMENT,
      contentOptions: { url },
    }),
  );

  yield take([PAYMENT_SUCCESS, PAYMENT_FAIL]);

  yield put(paymentVerification.actions.send({ paymentOrderId }));
}

function* handleFreePayment({ paymentStatus, paidAmount }) {
  if (paymentStatus === PAYMENT_STATUSES.PAID) {
    const appointment = yield select(selectActiveAppointment);
    yield put(
      appointmentDetailsActions.replaceAppointment({
        ...appointment,
        paymentStatus,
        paidAmount,
      }),
    );
    yield put(appointmentDetailsActions.confirmVisit());
    yield put(couponVerification.actions.clear());
    yield cancel();
  }
}

function* handleStartingPaymentProcess({ payload: { id: appointmentId } }) {
  const coupon = yield select(selectActiveAppointmentCoupon);
  yield put(paymentOrder.actions.send({ appointmentId, coupon }));

  const { succeed } = yield race({
    succeed: take(paymentOrder.consts.SUCCEED),
    rejected: take(paymentOrder.consts.REJECTED),
  });

  if (succeed) {
    const { payload } = succeed;

    const { id: paymentOrderId, payable, paymentMethod } = payload;

    yield put(appointmentDetailsActions.setOrderId(paymentOrderId));
    /**  100% COUPON   */
    yield call(handleFreePayment, payable);
    /**  PAYA PAYMENT  */
    if (paymentMethod === PAYMENT_METHODS.HOSTED_PAYMENT_PAGE) {
      yield call(runPayaPaymentFlow, paymentOrderId);
    } else {
      yield call(handleAccountFormPaymentSaga, { paymentOrderId, ...payable });
    }
  } else {
    yield put({
      type: error.consts.UPDATE,
      payload: { id: 'appointment.payment.errorProvider' },
    });
  }
}

function* handleFailedPayment() {
  // need to wait until paya modal component being closed.
  // cause they used the same component
  yield delay(0);

  const isAccountFormModalOpen = yield select(
    selectAppointmentPaymentInfoModalOpened,
  );

  if (isAccountFormModalOpen) {
    const appointmentId = yield select(selectActiveAppointmentId);
    const coupon = yield select(selectActiveAppointmentCoupon);

    if (appointmentId) {
      yield put(paymentOrder.actions.send({ appointmentId, coupon }));

      const { succeed } = yield race({
        succeed: take(paymentOrder.consts.SUCCEED),
        rejected: take(paymentOrder.consts.REJECTED),
      });

      if (succeed) {
        const newPaymentOrderId = get(succeed, 'payload.id');
        yield put(appointmentDetailsActions.setOrderId(newPaymentOrderId));
        yield put(
          appointmentPaymentInfoModalActions.updatePaymentOrderId(
            newPaymentOrderId,
          ),
        );
      }
    }
  }

  yield put(
    popup.actions.show({
      contentType: UI_POPUP_TYPES.MESSAGE,
      contentOptions: {
        message: { id: 'appointment.payment.errorMessage' },
        title: { id: 'appointment.payment.errorTitle' },
      },
    }),
  );
}

function* handleSuccessPayment() {
  const appointment = yield select(selectCurrentAppointment);
  const code = get(appointment, 'visitType.code');

  yield put(
    success.actions.update({
      id: 'appointment.payment.charged',
      title: 'appointment.payment.submitted',
    }),
  );

  if (code !== URGENT_VISIT_TYPE_CODE) {
    yield put(couponVerification.actions.clear());
  }
}

function* handleNotAvailableCouponPayment({
  payload: { couponsAvailable, automaticCouponAvailable } = {},
}) {
  const id = yield select(selectActiveAppointmentId);

  const paperworkAppointmentId = yield select(selectPaperworkAppointmentId);

  const isUrgentVisitFlow = yield select(selectProgressIsUrgentVisitFlow);

  if (!automaticCouponAvailable && !couponsAvailable && isUrgentVisitFlow) {
    return;
  }

  if (id && automaticCouponAvailable) {
    yield put(couponCalculation.actions.send({ appointmentId: id }));
    yield fork(handlePriceCalculations);
  }

  if (couponsAvailable === false && !automaticCouponAvailable) {
    yield put(
      appointmentDetailsActions.startPaymentProcess({
        id: id || paperworkAppointmentId,
      }),
    );
  }
}

function* handleAppointmentAccountFormPaymentSuccess({ payload }) {
  const paymentStatus = get(payload, 'payable.paymentStatus');

  const isFailedPaymentStatus =
    !paymentStatus || FAILED_PAYMENT_STATUSES.includes(paymentStatus);

  const isRoomInvitationPayment = yield select(
    selectRoomInvitationInfoAppointmentId,
  );

  if (isFailedPaymentStatus) {
    yield call(handleFailedPayment);
  } else if (isRoomInvitationPayment) {
    yield put(appointmentDetailsActions.paymentSuccess());
  } else {
    yield call(handleSuccessPayment);
  }

  yield put(appointmentPaymentInfoModalActions.close());
}

export default function* paymentSaga() {
  yield takeLatest(couponCalculation.consts.SEND, handleCouponCalculationSaga);
  yield takeLatest(START_PAYMENT_PROCESS, handleStartingPaymentProcess);
  yield takeLatest(
    [
      PAYMENT_FAIL,
      PAYMENT_TIMEOUT,
      appointmentAccountFormPaymentConst.REJECTED,
      appointmentAccountFormPaymentConst.ERROR_ENCOUNTERED,
    ],
    handleFailedPayment,
  );
  yield takeLatest(PAYMENT_SUCCESS, handleSuccessPayment);
  yield takeLatest(
    couponVerification.consts.SUCCEED,
    handleNotAvailableCouponPayment,
  );
  yield takeLatest(
    [appointmentAccountFormPaymentConst.SUCCEED],
    handleAppointmentAccountFormPaymentSuccess,
  );
}



// WEBPACK FOOTER //
// ./src/redux/healthboard/sagas/payment-saga.js